import React, { useContext, useEffect, useMemo } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Loader } from 'semantic-ui-react';

import { SatCoreComponent } from '../SatCoreRegistry';

const MODAL_HEIGHT = 220;
const MODAL_WIDTH = 420;

const GradebookBulkSelectProgress = observer((props) => {
  const {
    headerMsg: headerMsgProp,
    label: labelProp,
    loaded: loadedProp,
    open,
    t,
    total: totalProp
  } = props;
  const { gradebookManager } = useContext(MobXProviderContext);
  // memoize (via useMemo) so SatCoreComponent's per-call wrapper class is
  // created once; otherwise each modal re-render returns a new class and
  // React unmounts and remounts the child (losing its useState)
  const GenericModal = useMemo(() => SatCoreComponent('GenericModal'), []);
  // `open` lets other bulk flows reuse this modal. Gradebook select leaves it unset and follows its own fetch flag.
  const isControlled = open !== undefined;
  const isOpen = isControlled ? !!open : !!gradebookManager?.bulkSelectFetchInProgress;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const blockKeyboard = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', blockKeyboard, true);
    return () => window.removeEventListener('keydown', blockKeyboard, true);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const loaded = isControlled ? (loadedProp || 0) : (gradebookManager.assignmentInstances?.length || 0);
  const total = isControlled ? (totalProp || 0) : (gradebookManager.gradebookTableRowsTotalFromBackend || 0);
  const percent = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  const label = labelProp !== undefined && labelProp !== null ? labelProp : t('bulkSelectProgress', {
    defaultValue: 'Loading assignments {{loaded}} of {{total}}',
    loaded,
    total
  });
  const headerMsg = headerMsgProp || t('bulkSelectProgressHeader', { defaultValue: 'Loading assignments' });

  return (
    <GenericModal
      autoFocus={false}
      bodyMsg={(
        <div className='gradebook-bulk-select-progress'>
          <Loader active inline size='medium' />
          <div
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={percent}
            className='gradebook-bulk-select-progress-track'
            role='progressbar'>
            <div className='gradebook-bulk-select-progress-fill' style={{ width: `${percent}%` }} />
          </div>
          <span className='gradebook-bulk-select-progress-label'>{label}</span>
        </div>
      )}
      buttonKeyCancelLabel=''
      buttonKeyConfirmLabel=''
      className='GradebookBulkSelectProgressModal'
      closeOnDimmerClick={false}
      closeOnEscape={false}
      disableMove={true}
      headerMsg={headerMsg}
      initHeight={MODAL_HEIGHT}
      initWidth={MODAL_WIDTH}
      isOpen={true}
      minHeight={MODAL_HEIGHT}
      minWidth={MODAL_WIDTH}
      onClose={() => {}}
      overlay='medium'
      top={Math.max(24, (window.innerHeight - MODAL_HEIGHT) / 2)} />
  );
});

export default GradebookBulkSelectProgress;
